import { FC, ReactElement, useMemo } from 'react';

import { Customer } from '../../entities/@account/Customer/Customer';
import { Navigation, NavigationItem } from '../../entities/@navigation/Navigation/Navigation';
import { Link } from '../../entities/Link/Link';
import { AppRoute, appRoutes } from '../../entities/Routing/Routing';
import { generateUuid } from '../../helpers/string';
import { useDeviceWidth, useTrans } from '../../hooks';
import { DesktopMenu, MobileMenu } from './subcomponents';

import './Menu.scss';

interface MenuProps {
    isLoading: boolean;
    isAuthenticated: boolean;
    customer?: Customer;
    hasActiveSubNavigation: boolean;
    navigation?: Navigation;
    activeNavigation?: Navigation;
    topNavbarItems?: Link[];
    wishlistIsLoading: boolean;
    wishlistItemsCount: number;
    onNavigationChange: (navigation?: Navigation) => void;
    onBackButtonClick: () => void;
    className?: string;
}

export const Menu: FC<MenuProps> = ({
    isLoading,
    isAuthenticated,
    customer,
    hasActiveSubNavigation,
    navigation,
    activeNavigation,
    topNavbarItems,
    wishlistIsLoading,
    wishlistItemsCount,
    onNavigationChange,
    onBackButtonClick,
    className = '',
}): ReactElement => {
    const trans = useTrans();

    const { isMobile, isTabletPortrait } = useDeviceWidth();

    const isSmallScreen = useMemo((): boolean => (
        isMobile || isTabletPortrait
    ), [isMobile, isTabletPortrait]);

    const topNavbarNavigationItems = useMemo((): NavigationItem[] => {
        const items: NavigationItem[] = [];

        topNavbarItems?.forEach(item => {
            items.push({
                id: generateUuid(),
                to: item.href,
                label: item.label,
            });
        });

        return items;
    }, [topNavbarItems]);

    const accountNavigationItems = useMemo((): NavigationItem[] => {
        if (isAuthenticated) {
            return [
                {
                    id: AppRoute.account,
                    to: trans(appRoutes[AppRoute.accountDashboard].path),
                    label: trans(appRoutes[AppRoute.account].label),
                },
            ];
        }

        return [
            {
                id: AppRoute.login,
                to: trans(appRoutes[AppRoute.login].path),
                label: trans(appRoutes[AppRoute.login].label),
            },
        ];
    }, [isAuthenticated]);

    return (
        <section className={`menu ${className}`}>
            {isSmallScreen ? (
                <MobileMenu
                    isLoading={isLoading}
                    isAuthenticated={isAuthenticated}
                    hasActiveSubNavigation={hasActiveSubNavigation}
                    navigation={navigation}
                    activeNavigation={activeNavigation}
                    topNavbarItems={topNavbarNavigationItems}
                    wishlistIsLoading={wishlistIsLoading}
                    wishlistItemsCount={wishlistItemsCount}
                    accountNavigationItems={accountNavigationItems}
                    customer={customer}
                    onNavigationChange={onNavigationChange}
                    onBackButtonClick={onBackButtonClick}
                />
            ) : (
                <DesktopMenu
                    isAuthenticated={isAuthenticated}
                    navigation={navigation}
                    customer={customer}
                    wishlistIsLoading={wishlistIsLoading}
                    wishlistItemsCount={wishlistItemsCount}
                />
            )}
        </section>
    );
};
